import { jsPDF } from "jspdf";
import QRCodeLib from "qrcode";

/**
 * Génération de vrais fichiers PDF téléchargeables pour les billets SETRAG.
 * Utilisé partout où un billet doit être "téléchargé directement" :
 *   - juste après l'achat (Home.js / Dashboard.js)
 *   - depuis "Mes billets" (Dashboard.js)
 *   - depuis l'espace agent (AdminBookings.js)
 *
 * Contrairement à window.print() (qui ouvre la boîte de dialogue d'impression
 * et laisse l'utilisateur choisir "Enregistrer en PDF"), ceci génère
 * directement un fichier .pdf et déclenche son téléchargement - plus fiable
 * sur mobile et plus proche de ce qu'un utilisateur attend d'un bouton
 * "Télécharger".
 */

const GREEN_DK = [0, 77, 43];
const GREEN    = [0, 107, 60];
const GREEN_LT = [232, 245, 238];
const GRAY_TXT = [102, 102, 102];
const TEXT     = [26, 26, 26];

/**
 * @param {object} ticket
 *   { reference, passenger, seat, depart, arrivee, date, heure, train,
 *     classe, prix, tripType, isRoundTrip, qrPayload }
 */
async function drawTicket(doc, ticket, yStart = 15) {
  const x = 15, w = 180;
  let y = yStart;

  // Cadre
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, 100, 3, 3);

  // Bandeau haut
  doc.setFillColor(...GREEN_DK);
  doc.roundedRect(x, y, w, 18, 3, 3, "F");
  doc.rect(x, y + 9, w, 9, "F"); // carré les coins bas du bandeau
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("SETRAG", x + 6, y + 8);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text("TRANSGABONAIS", x + 6, y + 13);

  const badge = ticket.tripType === "RETOUR" ? "BILLET RETOUR" : "BILLET ALLER";
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(badge, x + w - 6, y + 8, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(ticket.reference || "", x + w - 6, y + 13, { align: "right" });

  // Trajet
  let ty = y + 28;
  doc.setTextColor(...GRAY_TXT);
  doc.setFontSize(7);
  doc.text("DÉPART", x + 6, ty);
  doc.text("ARRIVÉE", x + w - 6, ty, { align: "right" });

  doc.setTextColor(...GREEN_DK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(String(ticket.depart || ""), x + 6, ty + 6);
  doc.text(String(ticket.arrivee || ""), x + w - 6, ty + 6, { align: "right" });

  doc.setTextColor(...GREEN);
  doc.setFontSize(16);
  doc.text(String(ticket.heure || ""), x + 6, ty + 14);

  doc.setDrawColor(220, 220, 220);
  doc.setLineDashPattern([1, 1], 0);
  doc.line(x + 6, ty + 20, x + w - 6, ty + 20);
  doc.setLineDashPattern([], 0);

  // Infos passager / voyage
  let iy = ty + 28;
  const col1 = x + 6, col2 = x + w / 2 + 4;
  const field = (label, val, cx, cyy) => {
    doc.setTextColor(...GRAY_TXT);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(label, cx, cyy);
    doc.setTextColor(...TEXT);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(String(val ?? "-"), cx, cyy + 5);
  };
  field("PASSAGER", ticket.passenger, col1, iy);
  iy += 12;
  field("DATE", ticket.date, col1, iy);
  field("SIÈGE", `N°${ticket.seat}`, col2, iy);
  iy += 12;
  field("TRAIN", ticket.train, col1, iy);
  field("CLASSE", ticket.classe, col2, iy);

  // Bas : prix + QR
  const by = y + 100 - 22;
  doc.setFillColor(...GREEN_LT);
  doc.rect(x + 0.3, by, w - 0.6, 21.7, "F");
  doc.setTextColor(...GRAY_TXT);
  doc.setFontSize(7);
  doc.text("TARIF", x + 6, by + 8);
  doc.setTextColor(...GREEN);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(`${Number(ticket.prix || 0).toLocaleString("fr-FR")} FCFA`, x + 6, by + 16);

  if (ticket.qrPayload) {
    try {
      const qrDataUrl = await QRCodeLib.toDataURL(ticket.qrPayload, { width: 200, margin: 0, color: { dark: "#004D2B", light: "#ffffff" } });
      doc.addImage(qrDataUrl, "PNG", x + w - 26, by + 0.8, 20, 20);
    } catch {
      // QR non généré : le billet reste valide via sa référence, juste sans visuel scannable
    }
  }

  return y + 100;
}

/**
 * Génère un PDF contenant un ou plusieurs billets (un par page) et
 * déclenche son téléchargement.
 * @param {object[]} tickets
 * @param {string} filename
 */
export async function downloadTicketsPdf(tickets, filename = "billets-setrag.pdf") {
  if (!tickets || tickets.length === 0) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  for (let i = 0; i < tickets.length; i++) {
    if (i > 0) doc.addPage();
    await drawTicket(doc, tickets[i]);
  }

  doc.save(filename);
}

/**
 * Construit un objet "ticket" prêt pour downloadTicketsPdf à partir des
 * formes de données utilisées dans l'app (booking + trip + passager).
 * Préfère toujours booking.qrPayload (signé par le serveur, vérifiable
 * hors-ligne) à une reconstruction côté client.
 */
export function ticketFromBooking(booking, trip, passenger, tripType = "ALLER") {
  return {
    reference: booking?.reference || "",
    passenger: passenger?.fullName || booking?.passenger || booking?.fullName || "",
    seat: passenger?.seatNumber ?? booking?.seat ?? booking?.seatNumber,
    depart: trip?.departure?.name || trip?.departure,
    arrivee: trip?.arrival?.name || trip?.arrival,
    date: trip?.date,
    heure: trip?.departureTime,
    train: trip?.trainNumber,
    classe: trip?.travelClass,
    prix: trip?.price,
    tripType,
    qrPayload: booking?.qrPayload || null,
  };
}
